import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { Dialog } from 'primereact/dialog';
import { useGetOrganizationsQuery } from '../../api/organizationApi';
import {
  useGetAdminGroupsQuery,
  useUpdateAdminGroupMutation,
  useDeleteGroupMutation,
  useNotifyGroupMutation,
} from '../../api/groupApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import useAutoSelectFirst from '../../hooks/useAutoSelectFirst';
import StatusDots from '../../components/StatusDots/StatusDots';
import MessageField from '../../components/MessageField/MessageField';
import { messageMaxLengthForChannels } from '../../utils/messageLimits';
import useGroupActivityDots from '../../hooks/useGroupActivityDots';
import s from '../../styles/shared/page.module.css';
import buttons from '../../styles/shared/buttons.module.css';
import table from '../../styles/shared/table.module.css';

export default function AdminGroupsPage() {
  const { data: organizations, isLoading: loadingOrgs } =
    useGetOrganizationsQuery();
  const [organizationId, setOrganizationId] = useState('');
  const navigate = useNavigate();
  const showToast = useToast();
  const [notifyGroupTarget, setNotifyGroupTarget] = useState(null);
  const [message, setMessage] = useState('');
  const { activeGroupIds, clear: clearActivityDot } = useGroupActivityDots(organizationId);

  useAutoSelectFirst(organizations, organizationId, setOrganizationId);

  const {
    data: allGroups,
    isLoading: loadingGroups,
    isError: groupsError,
    error: groupsLoadError,
  } = useGetAdminGroupsQuery(organizationId, { skip: !organizationId });

  const groups = useMemo(() => (allGroups ?? []).filter((g) => !g.is_system), [allGroups]);

  const [updateGroup] = useUpdateAdminGroupMutation();
  const [deleteGroup] = useDeleteGroupMutation();
  const [notifyGroup, { isLoading: isSendingNotification }] = useNotifyGroupMutation();

  useEffect(() => {
    if (groupsError) showToast('error', 'Groups', errMsg(groupsLoadError, 'Could not load groups.'));
  }, [groupsError, groupsLoadError, showToast]);

  const tooltipRef = useRef(null);
  useEffect(() => {
    tooltipRef.current?.updateTargetEvents();
  }, [groups]);

  async function handleToggleEnabled(group) {
    try {
      await updateGroup({ id: group.id, organizationId, enabled: !group.enabled }).unwrap();
      showToast('success', 'Groups', group.enabled ? 'Group disabled.' : 'Group enabled.');
    } catch (err) {
      showToast('error', 'Groups', errMsg(err, 'Could not update group.'));
    }
  }

  async function handleDelete(group) {
    try {
      await deleteGroup({ id: group.id, organizationId }).unwrap();
      showToast('success', 'Groups', 'Group deleted.');
    } catch (err) {
      showToast('error', 'Groups', errMsg(err, 'Could not delete group.'));
    }
  }

  function openNotifyDialog(group) {
    setNotifyGroupTarget(group);
    setMessage('');
  }

  async function handleSendNotification() {
    try {
      const res = await notifyGroup({ groupId: notifyGroupTarget.id, message: message.trim() }).unwrap();
      const a = res.audience;
      showToast(
        'success',
        'Groups',
        `Notification sent — email: ${a.email}, sms: ${a.sms}, telegram: ${a.telegram}.`,
      );
      setNotifyGroupTarget(null);
    } catch (err) {
      showToast('error', 'Groups', errMsg(err, 'Could not send notification.'));
    }
  }

  const goEdit = (group) => navigate(`${group.id}/edit`);
  const goMessages = (group) => {
    clearActivityDot(group.id);
    navigate(`${group.id}/messages`);
  };

  const actionBodyTemplate = (group) => (
    <div className={table.actions}>
      <Button icon="pi pi-pencil" text className={table.iconNeutral} onClick={() => goEdit(group)} />
      <Button icon="pi pi-megaphone" text className={table.iconNeutral} onClick={() => openNotifyDialog(group)} />
      <span className={table.iconWithDot}>
        <Button icon="pi pi-comments" text className={table.iconNeutral} onClick={() => goMessages(group)} />
        {activeGroupIds.has(group.id) && <span className={table.newMessageDot} />}
      </span>
      <Button
        icon={`pi ${group.enabled ? 'pi-ban' : 'pi-check'}`}
        text
        className={group.enabled ? table.iconDisable : table.iconSuccess}
        onClick={() => handleToggleEnabled(group)}
      />
      <Button
        icon="pi pi-trash"
        text
        className={table.iconDanger}
        onClick={() => handleDelete(group)}
      />
    </div>
  );

  if (loadingOrgs) return <p className="textMuted">Loading…</p>;
  if (!organizations?.length)
    return (
      <div className={s.page}>
        <h1>Groups</h1>
        <p className={`${s.subtitle} textMuted`}>
          Create an organization first, then add groups to it.
        </p>
      </div>
    );

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <h1>Groups</h1>
        </div>
        <Button
          label="Add group"
          icon="pi pi-plus"
          className={buttons.btnPrimary}
          onClick={() => navigate('create', { state: { organizationId } })}
        />
      </div>

      <div className={`field ${s.orgSelect}`}>
        <label htmlFor="organization">Organization</label>
        <Dropdown
          inputId="organization"
          value={organizationId}
          onChange={(e) => setOrganizationId(e.value)}
          options={organizations.map((c) => ({ label: `${c.name} (${c.slug})`, value: c.id }))}
          className="w-full"
        />
      </div>

      <div className={table.tableWrapper}>
        <Tooltip ref={tooltipRef} target={`.${table.statusDot}`} position="left" />
        <DataTable
          value={groups}
          loading={loadingGroups}
          emptyMessage="No groups in this organization yet. Add one above."
          responsiveLayout="stack"
        >
          <Column
            field="name"
            header="Name"
            className={table.contactCol}
            body={(g) => <div className={table.cellInsider}>{g.name}</div>}
          />
          <Column
            header="Channels"
            className={`${s.mutedCell} ${table.contactCol}`}
            body={(g) => (
              <div className={table.cellInsider}>
                {(g.channels ?? []).filter((c) => c !== 'push').join(', ') || '—'}
                {!g.broadcast && ' · chat'}
              </div>
            )}
          />
          <Column
            header="Status"
            className={table.statusCol}
            body={(g) => (
              <div className={table.cellInsider}>
                <div className={`${table.statusBadgesDesktop} ${g.enabled ? s.statusOk : s.statusDisabled}`}>
                  {g.enabled ? 'enabled' : 'disabled'}
                </div>
                <StatusDots
                  items={[
                    {
                      label: g.enabled ? 'enabled' : 'disabled',
                      color: g.enabled ? 'var(--color-success)' : 'var(--color-error)',
                    },
                  ]}
                />
              </div>
            )}
          />
          <Column
            header="Actions"
            className={table.actionsCol}
            body={actionBodyTemplate}
            style={{ width: '400px' }}
          />
        </DataTable>
      </div>

      <Dialog
        header={`Send notification${notifyGroupTarget ? ` — ${notifyGroupTarget.name}` : ''}`}
        visible={Boolean(notifyGroupTarget)}
        onHide={() => setNotifyGroupTarget(null)}
        style={{ width: '520px' }}
        modal
      >
        <MessageField
          id="message"
          value={message}
          onChange={setMessage}
          maxLength={messageMaxLengthForChannels(notifyGroupTarget?.channels)}
        />
        <Button
          label="Send"
          className={buttons.saveBtn}
          loading={isSendingNotification}
          disabled={!message.trim()}
          onClick={handleSendNotification}
        />
      </Dialog>
    </div>
  );
}
